import React, { useState, useEffect } from "react";
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Drawer,
  Container,
  Button,
  List,
  ListItem,
  ListItemText,
  useMediaQuery,
  useTheme,
  Popper,
  Grow,
  Paper,
  ClickAwayListener,
  MenuList,
  MenuItem,
  Collapse,
  Link,
  Typography,
  Stack,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CloseIcon from "@mui/icons-material/Close";
import Logo from "./assets/Logo.png";
import DarkLogo from "./assets/darkLogo.png";

// Icons for mega menu
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import CodeIcon from '@mui/icons-material/Code';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import StorageIcon from '@mui/icons-material/Storage';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import FactoryIcon from '@mui/icons-material/Factory';

const currentPath = window.location.pathname;

// Function to check if current path matches any dark route pattern
const isDarkRoute = (path) => {
  const darkRoutes = [
    '/cart-recovery', '/voice', '/insurance', '/demo/noise', '/demo/lunazone',
    '/demo/lunaZone', '/demo/hdfc', '/demo/rlt-home', '/demo/cradlewise',
    '/demo/zoomcar', '/customer-agent', '/demo/epik', '/shopifypartners',
    '/comparisionPage', '/demo/magicleaf', '/compare/beanbag-ai-vs-vapi',
    '/compare/beanbag-ai-vs-openmic', '/compare/beanbag-ai-vs-retell',
    '/demo/europe', '/demo/hafla'
  ];
  return darkRoutes.some(route => path === route || path.startsWith(route + '/') || path.endsWith(route));
};

const isDarkHome = isDarkRoute(currentPath);

// NEW: AI-Services Focused Menu Structure
const navItems = {
  Solutions: [
    { label: "Strategic AI Consulting", href: "/solutions/strategic-consulting", icon: <LightbulbOutlinedIcon fontSize="small" />, desc: "Roadmaps & Assessments" },
    { label: "GenAI & LLM Engineering", href: "/solutions/genai-engineering", icon: <CodeIcon fontSize="small" />, desc: "Custom Models & RAG" },
    { label: "Intelligent Agents", href: "/solutions/intelligent-agents", icon: <PrecisionManufacturingIcon fontSize="small" />, desc: "Voice & Workflow Automation" },
    { label: "Data Engineering", href: "/solutions/data-engineering", icon: <StorageIcon fontSize="small" />, desc: "Pipelines & MLOps" },
  ],
  Industries: [
    { label: "Healthcare", href: "/#industries", icon: <LocalHospitalIcon fontSize="small" />, desc: "HIPAA-compliant AI" },
    { label: "Banking & FinTech", href: "/#industries", icon: <AccountBalanceIcon fontSize="small" />, desc: "Fraud & Compliance" },
    { label: "Retail & E-Commerce", href: "/#industries", icon: <ShoppingCartIcon fontSize="small" />, desc: "Personalization at Scale" },
    { label: "Manufacturing", href: "/#industries", icon: <FactoryIcon fontSize="small" />, desc: "Predictive Maintenance" },
  ],
  Resources: [
    { label: "Blog", href: "/blog" },
    { label: "Case Studies", href: "/case-studies" },
    { label: "Templates", href: "/templates" },
    { label: "Tools", href: "/tools" },
  ],
};

const CAL_ATTRS = {
  "data-cal-link": "nirman-agrawal/30-min-meeting",
  "data-cal-namespace": "30-min-meeting",
  "data-cal-config": '{"layout":"month_view","useSlotsViewOnSmallScreen":"true"}',
};

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (window.Cal) return;
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.innerHTML = `
      (function (C, A, L) {
        let p = function (a, ar) { a.q.push(ar); };
        let d = C.document;
        C.Cal = C.Cal || function () {
          let cal = C.Cal; let ar = arguments;
          if (!cal.loaded) { cal.ns = {}; cal.q = cal.q || []; d.head.appendChild(d.createElement("script")).src = A; cal.loaded = true; }
          if (ar[0] === L) {
            const api = function () { p(api, arguments); };
            const namespace = ar[1];
            api.q = api.q || [];
            if (typeof namespace === "string") { cal.ns[namespace] = cal.ns[namespace] || api; p(cal.ns[namespace], ar); p(cal, ["initNamespace", namespace]); } else p(cal, ar);
            return;
          }
          p(cal, ar);
        };
      })(window, "https://app.cal.com/embed/embed.js", "init");
      Cal("init", "30-min-meeting", { origin: "https://app.cal.com" });
      Cal.ns["30-min-meeting"]("ui", { "hideEventTypeDetails": false, "layout": "month_view" });
    `;
    document.head.appendChild(script);
  }, []);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [activeDropdown, setActiveDropdown] = useState(null);
  const [dropdownAnchors, setDropdownAnchors] = useState({});
  const [activeMobileDropdown, setActiveMobileDropdown] = useState(null);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  const handleMouseEnter = (event, page) => {
    setActiveDropdown(page);
    setDropdownAnchors({ ...dropdownAnchors, [page]: event.currentTarget });
  };

  const handleMouseLeave = () => setActiveDropdown(null);
  const handleClickAway = () => setActiveDropdown(null);
  const handleMobileDropdownToggle = (page) => setActiveMobileDropdown(activeMobileDropdown === page ? null : page);

  const handleNavigation = (href) => {
    setActiveDropdown(null);
    setMobileOpen(false);
    window.location.href = href;
  };

  // Mobile Drawer
  const drawer = (
    <Box sx={{ textAlign: "center", p: 2, height: "100%", display: "flex", flexDirection: "column" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, px: 1 }}>
        <Box component={Link} href="/" sx={{ display: "flex", alignItems: "center" }}>
          <img src={DarkLogo} alt="Beanbag AI" style={{ height: isSmallMobile ? "32px" : "40px" }} />
        </Box>
        <IconButton onClick={handleDrawerToggle} sx={{ color: "white" }}><CloseIcon /></IconButton>
      </Box>

      <List sx={{ flexGrow: 1 }}>
        {Object.keys(navItems).map((page) => (
          <React.Fragment key={page}>
            <ListItem button onClick={() => handleMobileDropdownToggle(page)} sx={{ justifyContent: "space-between", py: 1.5 }}>
              <ListItemText primary={page} primaryTypographyProps={{ color: "white", fontWeight: 600, fontSize: "1.1rem" }} />
              {activeMobileDropdown === page ? <KeyboardArrowUpIcon sx={{ color: "white" }} /> : <KeyboardArrowDownIcon sx={{ color: "white" }} />}
            </ListItem>
            <Collapse in={activeMobileDropdown === page} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {navItems[page].map((item) => (
                  <ListItem key={item.label} button onClick={() => handleNavigation(item.href)} sx={{ pl: 4, py: 1 }}>
                    <ListItemText primary={item.label} primaryTypographyProps={{ color: "rgba(255,255,255,0.7)", fontSize: "0.95rem" }} />
                  </ListItem>
                ))}
              </List>
            </Collapse>
          </React.Fragment>
        ))}
      </List>

      <Box sx={{ py: 2 }}>
        <Button
          variant="contained"
          fullWidth
          sx={{
            background: 'linear-gradient(135deg, #E2684A 0%, #7C3AED 100%)',
            color: "white",
            py: 1.5,
            borderRadius: "12px",
            fontWeight: 600,
            textTransform: "none",
            fontSize: "1rem",
          }}
          {...CAL_ATTRS}
        >
          Book a Strategy Call
        </Button>
      </Box>
    </Box>
  );

  return (
    <AppBar
      position="fixed"
      sx={{
        // GLASSMORPHISM EFFECT
        bgcolor: "rgba(3, 0, 20, 0.7)",
        backdropFilter: "blur(20px)",
        borderBottom: "1px solid rgba(255, 255, 255, 0.05)",
        boxShadow: "none",
        py: 1,
      }}
    >
      <Container maxWidth="xl">
        <Toolbar disableGutters sx={{ display: "flex", justifyContent: "space-between", minHeight: { xs: "56px", sm: "64px" } }}>
          {/* Logo */}
          <Box component={Link} href="/" sx={{ display: "flex", alignItems: "center", textDecoration: "none" }}>
            <img src={isDarkHome ? DarkLogo : Logo} alt="Beanbag AI" style={{ height: isSmallMobile ? "32px" : "40px" }} />
          </Box>

          {/* Mobile Menu Button */}
          {isMobile && (
            <IconButton onClick={handleDrawerToggle} sx={{ color: isDarkHome ? "black" : "white" }}>
              <MenuIcon />
            </IconButton>
          )}

          {/* Desktop Navigation */}
          {!isMobile && (
            <ClickAwayListener onClickAway={handleClickAway}>
              <Box sx={{ display: "flex", gap: 4, alignItems: "center" }}>
                {Object.keys(navItems).map((page) => (
                  <Box
                    key={page}
                    sx={{ position: "relative" }}
                    onMouseEnter={(e) => handleMouseEnter(e, page)}
                    onMouseLeave={handleMouseLeave}
                  >
                    <Button
                      sx={{
                        color: "rgba(255,255,255,0.85)",
                        textTransform: "none",
                        fontSize: "1rem",
                        fontWeight: 500,
                        '&:hover': { color: 'white' }
                      }}
                      endIcon={activeDropdown === page ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    >
                      {page}
                    </Button>

                    {/* MEGA MENU DROPDOWN */}
                    <Popper open={activeDropdown === page} anchorEl={dropdownAnchors[page]} placement="bottom-start" transition disablePortal sx={{ zIndex: 1300 }}>
                      {({ TransitionProps }) => (
                        <Grow {...TransitionProps} style={{ transformOrigin: "top left" }}>
                          <Paper
                            sx={{
                              mt: 2,
                              p: 2,
                              minWidth: page === "Resources" ? "200px" : "320px",
                              bgcolor: "rgba(10, 5, 30, 0.95)",
                              backdropFilter: "blur(20px)",
                              border: "1px solid rgba(255,255,255,0.1)",
                              borderRadius: 3,
                              boxShadow: "0 20px 40px rgba(0,0,0,0.5)",
                            }}
                            onMouseEnter={() => setActiveDropdown(page)}
                            onMouseLeave={() => setActiveDropdown(null)}
                          >
                            <MenuList sx={{ p: 0 }}>
                              {navItems[page].map((item) => (
                                <MenuItem
                                  key={item.label}
                                  onClick={() => handleNavigation(item.href)}
                                  sx={{
                                    py: 1.5,
                                    px: 2,
                                    borderRadius: 2,
                                    '&:hover': { bgcolor: 'rgba(226, 104, 74, 0.1)' }
                                  }}
                                >
                                  <Stack direction="row" spacing={2} alignItems="center">
                                    {item.icon && (
                                      <Box sx={{ color: '#E2684A', display: 'flex' }}>{item.icon}</Box>
                                    )}
                                    <Box>
                                      <Typography variant="body1" sx={{ color: "white", fontWeight: 500 }}>{item.label}</Typography>
                                      {item.desc && <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.5)" }}>{item.desc}</Typography>}
                                    </Box>
                                  </Stack>
                                </MenuItem>
                              ))}
                            </MenuList>
                          </Paper>
                        </Grow>
                      )}
                    </Popper>
                  </Box>
                ))}
              </Box>
            </ClickAwayListener>
          )}

          {/* GRADIENT CTA BUTTON */}
          {!isMobile && (
            <Button
              variant="contained"
              sx={{
                background: 'linear-gradient(135deg, #E2684A 0%, #7C3AED 100%)',
                color: "white",
                px: 3,
                py: 1,
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.95rem",
                boxShadow: '0 0 20px rgba(226, 104, 74, 0.3)',
                '&:hover': {
                  boxShadow: '0 0 30px rgba(226, 104, 74, 0.5)',
                  background: 'linear-gradient(135deg, #E2684A 0%, #7C3AED 100%)',
                }
              }}
              {...CAL_ATTRS}
            >
              Book a Call
            </Button>
          )}
        </Toolbar>
      </Container>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": { width: { xs: "100%", sm: 320 }, bgcolor: "#030014" },
        }}
      >
        {drawer}
      </Drawer>
    </AppBar>
  );
};

export default Navbar;